/**
 * Shopping Break (No Spend Weekend) — Screen Time authorization stabilization.
 *
 * On iOS the FamilyControls `AuthorizationCenter.shared.authorizationStatus`
 * can momentarily report `notDetermined` right after a cold launch — before
 * its persisted state has finished loading — even for users who previously
 * granted Screen Time access. The Shopping Break view reads authorization once
 * on mount (its `visibilitychange` re-check never fires on a cold launch, since
 * the view is already visible), so that stale value stuck and the "Grant Screen
 * Time access" button was shown to already-authorized users
 * (card WF5x4If150tnYYcbDsuU). Tapping it then re-requested authorization,
 * which on some iOS versions revokes the existing grant.
 *
 * The fix: when the first read comes back `notDetermined` on iOS, re-read a few
 * times until the status resolves to a definitive value. A genuine first-time
 * user simply stays `notDetermined` after the bounded retries (small,
 * unnoticeable delay before the grant prompt appears).
 */

import type { BlockingPermissionState } from '../plugins/ShoppingBlockerPlugin';

export const AUTH_STABILIZE_RETRIES = 4;
export const AUTH_STABILIZE_DELAY_MS = 120;

const defaultWait = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface StabilizeOptions {
  /** Only iOS suffers the cold-launch staleness; elsewhere the first read is authoritative. */
  stabilize: boolean;
  retries?: number;
  delayMs?: number;
  /** Injectable for tests. */
  wait?: (ms: number) => Promise<void>;
}

/**
 * Read native blocking authorization, re-reading while it reports
 * `notDetermined` (iOS cold-launch staleness). `approved`, `denied`, and
 * `unavailable` are returned immediately — only the ambiguous `notDetermined`
 * is retried.
 */
export async function stabilizeAuthorization(
  read: () => Promise<BlockingPermissionState>,
  opts: StabilizeOptions
): Promise<BlockingPermissionState> {
  const retries = opts.retries ?? AUTH_STABILIZE_RETRIES;
  const delayMs = opts.delayMs ?? AUTH_STABILIZE_DELAY_MS;
  const wait = opts.wait ?? defaultWait;

  let state = await read();
  if (!opts.stabilize) return state;

  for (let attempt = 0; attempt < retries && state.primary === 'notDetermined'; attempt++) {
    await wait(delayMs);
    state = await read();
  }
  return state;
}
